'use client';

import { memo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Pencil } from 'lucide-react';
import { useControlManagement } from '@/features/control/hooks/use-control-management';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui';
import { StructureContent } from '../settings-content/StructureContent';
import type { Control, Angle } from '@/types/concept';
import type { RenderControl } from '@/types/render';
import { SettingsSectionRow } from './SettingsSection';
import { ThumbStrip } from './ReferenceSummary';

interface StructureSectionProps {
  controls: Control[];
  selectedControlIds: Array<RenderControl | string>;
  angle?: Angle | null;
  /** Backdrop for selected card / preview / editor — caller resolves by mode. */
  backdropUrl?: string | null;
  onControlsChange: (controls: RenderControl[]) => void;
  onControlsUpdate?: (controls: Control[]) => void;
  hasChanges?: boolean;
}

function StructureSectionComponent({
  controls: rawControls,
  selectedControlIds,
  angle,
  backdropUrl,
  onControlsChange,
  onControlsUpdate,
  hasChanges = false,
}: StructureSectionProps) {
  const t = useTranslations('render.settings.control');
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const hookResult = useControlManagement({
    angle: angle ?? null,
    rawControls,
    selectedControlIds,
    onControlsChange,
    onControlsUpdate,
  });

  // Selected sketch → its image as a circular thumbnail (mirrors the
  // reference rows). Nothing selected → empty trailing slot (no "None" text).
  const controlImageUrl = hookResult.hasSelectedControl
    ? hookResult.selectedControl?.attributes.file?.url
    : undefined;

  return (
    <>
      <SettingsSectionRow
        icon={Pencil}
        title={t('title')}
        subtitleContent={
          controlImageUrl ? (
            <ThumbStrip images={[controlImageUrl]} shape="circle" darkBackground />
          ) : undefined
        }
        hasChanges={hasChanges}
        onClick={() => setIsSheetOpen(true)}
      />

      <NestedSheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <NestedSheetContent>
          <NestedSheetHeader
            trailingAction={
              hookResult.hasSelectedControl ? (
                <button
                  onClick={() => onControlsChange([])}
                  className="text-primary hover:text-primary/80 transition-colors text-form-label"
                >
                  {t('clearSelection')}
                </button>
              ) : undefined
            }
          >
            <NestedSheetTitle>{t('title')}</NestedSheetTitle>
          </NestedSheetHeader>

          <NestedSheetBody className="px-4">
            <StructureContent
              controls={hookResult.controls}
              selectedControl={hookResult.selectedControl}
              hasSelectedControl={hookResult.hasSelectedControl}
              selectedControlIds={selectedControlIds}
              angle={angle}
              backdropUrl={backdropUrl}
              isEditingControl={hookResult.isEditingControl}
              editingControlBase={hookResult.editingControlBase}
              unusedControlsCount={hookResult.unusedControlsCount}
              deleteConfirmation={hookResult.deleteConfirmation}
              deleting={hookResult.deleting}
              onControlsChange={onControlsChange}
              handleCreateControl={hookResult.handleCreateControl}
              handleEditControl={hookResult.handleEditControl}
              handleEditControlFor={hookResult.handleEditControlFor}
              handleSaveEditedControl={hookResult.handleSaveEditedControl}
              handleDeleteControl={hookResult.handleDeleteControl}
              handleConfirmDeleteControl={hookResult.handleConfirmDeleteControl}
              handleCancelDelete={hookResult.handleCancelDelete}
              handleControlToggle={hookResult.handleControlToggle}
              cancelEditing={hookResult.cancelEditing}
              variant="compact"
            />
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>
    </>
  );
}

export const StructureSection = memo(
  StructureSectionComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.angle?.id === nextProps.angle?.id &&
      prevProps.backdropUrl === nextProps.backdropUrl &&
      prevProps.hasChanges === nextProps.hasChanges &&
      prevProps.selectedControlIds.join(',') ===
        nextProps.selectedControlIds.join(',') &&
      prevProps.controls.length === nextProps.controls.length &&
      prevProps.controls.every((c, i) => c.id === nextProps.controls[i]?.id) &&
      prevProps.onControlsChange === nextProps.onControlsChange &&
      prevProps.onControlsUpdate === nextProps.onControlsUpdate
    );
  }
);
